import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import axiosInstance from '../../api/axios';

/**
 * Auto-rotating teaser of the 3 newest blog posts on the homepage.
 * 
 * Behaviour:
 * - Fetches up to 3 latest published posts from /api/blog/posts
 * - Auto-rotates every 7 seconds, pauses on hover
 * - Dots + chevron arrows for manual navigation
 * - "Weiterlesen"-Button links to /blog/{post.id} (full post)
 * - If 0 posts: section is not rendered (clean homepage)
 * - Mobile-optimised: single column, large tap-targets, no horizontal overflow
 */
const BlogTeaserSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get('/blog/posts?limit=3');
        if (alive) setPosts(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (alive) setPosts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (paused || posts.length <= 1) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % posts.length);
    }, 7000);
    return () => clearInterval(id);
  }, [paused, posts.length]);

  if (loading) return null;
  if (!posts.length) return null;

  const next = () => setActiveIdx((i) => (i + 1) % posts.length);
  const prev = () => setActiveIdx((i) => (i - 1 + posts.length) % posts.length);

  const post = posts[activeIdx];
  // Excerpt: strip HTML, cap at ~180 chars, append ellipsis
  const stripHtml = (s) => (s || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  const plainExcerpt = stripHtml(post.excerpt);
  const plainContent = stripHtml(post.content);
  const excerpt = plainExcerpt || (plainContent.length > 200 ? plainContent.slice(0, 200).trimEnd() + '…' : plainContent);
  const heroImg = post.featured_image || post.cover_image || post.image || (post.images && post.images[0]) || null;

  return (
    <section
      className="py-12 sm:py-16 px-4 sm:px-6 bg-[#FAF5EE]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="home-blog-teaser"
      aria-label="Aus unserem Blog"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-[#C4A084] flex-shrink-0" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-[#3D3D3D] break-words">
              Aus unserem Blog
            </h2>
          </div>
          <Link
            to="/blog"
            className="text-xs sm:text-sm font-medium text-[#8B6F4D] hover:text-[#5D4E3C] underline whitespace-nowrap flex-shrink-0"
            data-testid="blog-teaser-all-posts"
          >
            Alle Beiträge →
          </Link>
        </div>

        <article
          key={post.id}
          className="bg-white shadow-sm overflow-hidden grid grid-cols-1 md:grid-cols-2 transition-opacity duration-500 animate-fade-in"
          data-testid={`blog-teaser-active-${post.id}`}
        >
          {heroImg ? (
            <Link to={`/blog/${post.id}`} className="block aspect-[16/10] md:aspect-auto bg-[#F5EDE4] overflow-hidden">
              <img
                src={heroImg}
                alt={post.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </Link>
          ) : (
            <div className="hidden md:flex items-center justify-center bg-[#F5EDE4]">
              <BookOpen className="h-12 w-12 text-[#C4A084]" />
            </div>
          )}
          <div className="p-5 sm:p-7 flex flex-col justify-between gap-4 min-w-0">
            <div className="min-w-0">
              {post.category && (
                <span className="inline-block text-[10px] sm:text-xs uppercase tracking-wider text-[#8B6F4D] font-semibold mb-2">
                  {post.category}
                </span>
              )}
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#3D3D3D] leading-snug mb-3 break-words" data-testid="blog-teaser-title">
                {post.title}
              </h3>
              <p className="text-sm sm:text-base text-[#5D4E3C] leading-relaxed break-words" data-testid="blog-teaser-excerpt">
                {excerpt}
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
              <Link
                to={`/blog/${post.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C4A084] hover:bg-[#B89968] text-white font-medium text-sm transition-colors"
                data-testid="blog-teaser-readmore"
              >
                Weiterlesen <ArrowRight className="h-4 w-4" />
              </Link>
              {post.created_at && (
                <span className="text-[11px] sm:text-xs text-[#8B7355]">
                  {new Date(post.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>
        </article>

        {/* Pagination controls */}
        {posts.length > 1 && (
          <div className="flex items-center justify-center gap-3 mt-5 sm:mt-6">
            <button
              onClick={prev}
              className="p-2 rounded-full hover:bg-white transition-colors"
              aria-label="Vorheriger Beitrag"
              data-testid="blog-teaser-prev"
            >
              <ChevronLeft className="h-5 w-5 text-[#5D4E3C]" />
            </button>
            <div className="flex items-center gap-2">
              {posts.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActiveIdx(i)}
                  aria-label={`Beitrag ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === activeIdx ? 'w-8 bg-[#C4A084]' : 'w-2 bg-[#D4C4B0] hover:bg-[#C4A084]'}`}
                  data-testid={`blog-teaser-dot-${i}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="p-2 rounded-full hover:bg-white transition-colors"
              aria-label="Nächster Beitrag"
              data-testid="blog-teaser-next"
            >
              <ChevronRight className="h-5 w-5 text-[#5D4E3C]" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogTeaserSection;
